import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import TooltipWrapper from '@/components/ui/tooltip';
import {
  getPositionFunding,
  getPositionInitMargin,
  getPositionUnrealizedPnL,
} from '@/features/account/utils/math';
import PnL from '@/features/portfolio/components/pnl';
import {
  createOrderInputSchema,
  useCreateOrder,
} from '@/features/trade/order/api/create-order';
import {
  getIsMigrationTradingBlocked,
  getMigrationBlockButtonKey,
  useMigrationTradingBlock,
} from '@/hooks/use-migration';
import {
  getIsWeekendTradingBlocked,
  useWeekendTrading,
} from '@/hooks/use-weekend-trading';
import { cn, getDefaultBigDecimal } from '@/lib/utils';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import {
  getIsStatusEval,
  getIsStatusFundedOrEval,
} from '@/features/vest-capital/utils/status';
import { useMarketStore } from '@/store/use-markets-store';
import { EventType } from '@/types';
import { OrderType } from '@/types/enums';
import { sendEvent } from '@/utils/analytics';
import { abbrFromSym } from '@/utils/token-symbol';
import {
  BigDecimal,
  bigIntToDecimalStr,
  formatBigInt,
  formatNumber,
  parseDecimalToBigInt,
  parseInputToDecimalStr,
} from '@/utils/value-format';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle } from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { validateFinalLimitPrice } from '../utils/helpers';
import {
  calculateSlippageAmount,
  getEstFillPrice,
  getBboPrice,
  getFee,
  getLimitPriceFromMaxSlippage,
  getSlippage,
  getSlippageReferencePrice,
} from '../utils/math';
import { getProjectedMidSlippageAmount } from '../utils/slippage-estimation';
import LimitsBreachConfirm, {
  getLimitsBreachInfo,
  LimitsBreachInfo,
} from './limits-breach-confirm';
import {
  getDefaultMaxSlippageValue,
  MaxSlippageInput,
} from './max-slippage-input';
import OrderConfirm, { getShouldConfirm } from './order-confirm';
import type { OrderFormSchema } from './order-form';
import { Tabs, TabsList, TabsTrigger } from './order-tabs';
import { PriceInput } from './price-input';
import SizeInput from './size-input';

interface CloseDialogProps {
  symbol: string;
  disabled?: boolean;
}

const closeFormSchema = z.object({
  order: createOrderInputSchema,
});

type CloseFormValues = z.infer<typeof closeFormSchema>;

const LazyCloseDialogContent = lazy(() =>
  Promise.resolve({
    default: CloseDialogContent,
  }),
);

export const CloseDialog = ({ symbol, disabled }: CloseDialogProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { isBlocked: isMigrationBlocked } = useMigrationTradingBlock();

  useEffect(() => {
    if (isMigrationBlocked) setOpen(false);
  }, [isMigrationBlocked]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline2"
          size="lg"
          disabled={disabled || isMigrationBlocked}
        >
          {t('closeDialog.close')}
        </Button>
      </DialogTrigger>
      {open && (
        <Suspense fallback={null}>
          <LazyCloseDialogContent
            symbol={symbol}
            open={open}
            onOpenChange={setOpen}
          />
        </Suspense>
      )}
    </Dialog>
  );
};

interface CloseDialogContentProps extends CloseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CloseDialogContent = ({
  symbol,
  onOpenChange,
}: CloseDialogContentProps) => {
  const position = useCurrentAccount((state) => {
    const position = state.positions[symbol];
    return {
      size: position.size,
      isLong: position.isLong,
      initMarginRatio: position.initMarginRatio,
      entryFunding: position.entryFunding,
      cost: position.cost,
    };
  });

  const { isEval, isFundedOrEval } = useAccountStore(
    useShallow((state) => {
      const capitalStatus = state.getCapitalStatus(state.address);
      return {
        isEval: getIsStatusEval(capitalStatus),
        isFundedOrEval: getIsStatusFundedOrEval(capitalStatus),
      };
    }),
  );
  const { isBlocked: isWeekendBlocked, reopenLabel } =
    useWeekendTrading(symbol);
  const { isBlocked: isMigrationBlocked, reason: migrationReason } =
    useMigrationTradingBlock();

  const { t } = useTranslation();
  const { bboPrice, midPrice, priceDecimals, sizeDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      bboPrice: getBboPrice(symbol, !position.isLong),
      midPrice: state.marketData.midPrice,
      priceDecimals: state.marketSpec.priceDecimals,
      sizeDecimals: state.marketSpec.sizeDecimals,
    })),
  );

  const form = useForm<CloseFormValues>({
    resolver: zodResolver(closeFormSchema),
    defaultValues: {
      order: {
        symbol,
        isBuy: !position.isLong,
        size: bigIntToDecimalStr(position.size, sizeDecimals),
        orderType: OrderType.MARKET,
        limitPrice: bboPrice
          ? getLimitPriceFromMaxSlippage(
              !position.isLong,
              parseDecimalToBigInt(
                getDefaultMaxSlippageValue(symbol).toString(),
                2n,
              ),
              bboPrice,
              priceDecimals,
            ).decimal
          : '',
        reduceOnly: true,
      },
    },
  });

  const orderType = form.watch('order.orderType');
  const limitPrice = form.watch('order.limitPrice');

  const isMarketOrder = orderType === OrderType.MARKET;

  useEffect(() => {
    if (!isMarketOrder)
      form.setValue(
        'order.limitPrice',
        bigIntToDecimalStr(midPrice, priceDecimals),
      );
  }, [isMarketOrder]);

  // handle size changes
  const orderSize = parseDecimalToBigInt(
    form.watch('order.size'),
    sizeDecimals,
  );
  const book = useStore(useMarketStore(symbol), (state) => state.book);

  const {
    unrealizedPnl,
    estFillPrice,
    slippage,
    funding,
    fee,
    enoughLiquidity,
    slippageAmount,
  } = useMemo(() => {
    let enoughLiquidity = true;

    const marketFillPrice = isEval ? bboPrice : midPrice;

    if (orderSize === 0n) {
      return {
        unrealizedPnl: {
          pnl: getDefaultBigDecimal(),
          roePct: getDefaultBigDecimal(2),
        },
        estFillPrice: isMarketOrder
          ? marketFillPrice ?? undefined
          : parseDecimalToBigInt(limitPrice, priceDecimals),
        slippage: { bigint: 0n, decimal: '0' } as BigDecimal,
        funding: getDefaultBigDecimal(),
        fee: getDefaultBigDecimal(),
        enoughLiquidity,
        slippageAmount: 0n,
      };
    }

    let estFillPrice: bigint | null;
    let slippageFillPrice: bigint | null;
    let visibleCost = 0n;
    let visibleFilled = 0n;

    if (!isMarketOrder) {
      estFillPrice = parseDecimalToBigInt(limitPrice, priceDecimals);
      slippageFillPrice = estFillPrice;
    } else {
      const fillPrice = getEstFillPrice(
        !position.isLong,
        orderSize,
        book,
        priceDecimals,
        isEval,
      );
      enoughLiquidity = fillPrice?.enoughLiquidity ?? true;
      estFillPrice = isEval
        ? marketFillPrice ?? null
        : fillPrice?.bigint ?? null;
      slippageFillPrice = fillPrice?.bigint ?? null;
      visibleCost = fillPrice?.visibleCost ?? 0n;
      visibleFilled = fillPrice?.visibleFilled ?? 0n;
    }

    if (estFillPrice === null) {
      return {
        unrealizedPnl: undefined,
        estFillPrice: undefined,
        slippage: undefined,
        funding: getPositionFunding(
          symbol,
          position.isLong,
          orderSize,
          position.entryFunding,
        ),
        fee: getDefaultBigDecimal(),
        enoughLiquidity,
        slippageAmount: null,
      };
    }

    const fee = getFee(symbol, orderSize, estFillPrice);
    const funding = getPositionFunding(
      symbol,
      position.isLong,
      orderSize,
      position.entryFunding,
    );

    const slippageAmount = isEval
      ? calculateSlippageAmount(!position.isLong, orderSize, bboPrice, midPrice)
      : getProjectedMidSlippageAmount(
          !position.isLong,
          orderSize,
          visibleCost,
          visibleFilled,
          book,
        );

    const cost = (position.cost * orderSize) / position.size;

    const initMargin = getPositionInitMargin(
      symbol,
      cost,
      position.initMarginRatio,
    );

    const unrealizedPnl = getPositionUnrealizedPnL(
      symbol,
      {
        ...position,
        size: orderSize,
        cost,
        initMargin,
      },
      estFillPrice,
    );

    return {
      unrealizedPnl,
      estFillPrice,
      fee,
      funding,
      slippageAmount,
      slippage:
        slippageFillPrice === null
          ? undefined
          : getSlippage(
              symbol,
              !position.isLong,
              slippageFillPrice,
              isEval
                ? undefined
                : {
                    size: orderSize,
                    visibleCost,
                    visibleFilled,
                    book,
                  },
            ),
      enoughLiquidity,
    };
  }, [
    symbol,
    midPrice,
    orderSize,
    limitPrice,
    position,
    isMarketOrder,
    isEval,
    book,
    bboPrice,
  ]);

  const { mutate: createOrder } = useCreateOrder();

  const formRef = useRef<HTMLFormElement | null>(null);
  const isConfirmSubmitted = useRef<boolean>(false);
  const isLimitsBreachConfirmSubmitted = useRef<boolean>(false);
  const preConfirmLimitPrice = useRef<string | null>(null);
  const wasConfirmSubmitted = useRef<boolean>(false);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLimitsBreachConfirmOpen, setIsLimitsBreachConfirmOpen] =
    useState(false);
  const [limitsBreachInfo, setLimitsBreachInfo] =
    useState<LimitsBreachInfo | null>(null);

  const restoreLimitPriceOnCancel = () => {
    if (preConfirmLimitPrice.current !== null) {
      form.setValue('order.limitPrice', preConfirmLimitPrice.current);
      preConfirmLimitPrice.current = null;
    }
  };

  const resetConfirmFlags = () => {
    isConfirmSubmitted.current = false;
    isLimitsBreachConfirmSubmitted.current = false;
  };

  function onSubmit(values: CloseFormValues) {
    setIsSubmitSuccessful(false);

    // Belt and braces: the submit button is already disabled while the weekend
    // pause is on, but a queued confirm dialog can resolve after it starts.
    if (getIsWeekendTradingBlocked(symbol) || getIsMigrationTradingBlocked()) {
      resetConfirmFlags();
      onOpenChange(false);
      return;
    }

    if (
      !isConfirmSubmitted.current &&
      getShouldConfirm(values as OrderFormSchema)
    ) {
      preConfirmLimitPrice.current = values.order.limitPrice;

      sendEvent(EventType.SLIPPAGE_EXCEEDED, {
        symbol,
        slippage: slippage?.decimal ?? '-',
        enough_liquidity: enoughLiquidity,
        stage: 'close',
      });

      setIsConfirmOpen(true);
      return;
    }

    if (
      values.order.orderType === OrderType.MARKET &&
      (!getSlippageReferencePrice(values.order.symbol, values.order.isBuy) ||
        parseDecimalToBigInt(values.order.limitPrice, priceDecimals) <= 0n)
    ) {
      return;
    }

    const breachInfo = getLimitsBreachInfo(values as OrderFormSchema);

    if (breachInfo && !isLimitsBreachConfirmSubmitted.current) {
      setLimitsBreachInfo(breachInfo);
      setIsLimitsBreachConfirmOpen(true);
      return;
    }

    resetConfirmFlags();

    const limitPrice = validateFinalLimitPrice(symbol, values.order.limitPrice);

    const validatedOrderParams = {
      ...values.order,
      size: parseInputToDecimalStr(values.order.size, sizeDecimals),
      limitPrice,
    };
    createOrder({
      order: validatedOrderParams,
      showToast: true,
      isClose: true,
    });
    onOpenChange(false);
    form.reset();
    setIsSubmitSuccessful(false);
    setTimeout(() => setIsSubmitSuccessful(true));
  }

  const isLimitPriceInvalid = useMemo(() => {
    if (orderType !== OrderType.LIMIT) return false;
    const price = parseDecimalToBigInt(limitPrice, priceDecimals);
    if (
      price >= 10n * midPrice ||
      price <= midPrice / 10n ||
      (position.isLong && price <= midPrice / 2n)
    )
      return true;
    return false;
  }, [orderType, position.isLong, limitPrice, midPrice, priceDecimals]);

  const onOrderTypeChange = (value: string) => {
    setIsSubmitSuccessful(false);
    form.setValue(`order.orderType`, value as OrderType);

    const { marketData, marketSpec } = useMarketStore(symbol).getState();

    const limitPrice = bigIntToDecimalStr(
      marketData.midPrice,
      marketSpec.priceDecimals,
    );

    if (value === OrderType.LIMIT) {
      form.setValue('order.limitPrice', limitPrice);
      form.setValue('order.timeInForce', 'GTC');
    } else {
      const referencePrice = getSlippageReferencePrice(
        symbol,
        !position.isLong,
      );
      const limitPriceWithSlippage = referencePrice
        ? getLimitPriceFromMaxSlippage(
            !position.isLong,
            parseDecimalToBigInt(
              getDefaultMaxSlippageValue(symbol).toString(),
              2n,
            ),
            referencePrice,
            marketSpec.priceDecimals,
          ).decimal
        : '';

      form.setValue('order.limitPrice', limitPriceWithSlippage);
      form.setValue('order.timeInForce', undefined);
    }
  };

  const submitButton = (
    <Button
      type="submit"
      className="h-16 w-full rounded-none py-6 font-mono text-lg font-normal tracking-wider"
      disabled={
        orderSize === 0n ||
        orderSize > position.size ||
        (isMarketOrder && isEval && !bboPrice) ||
        isLimitPriceInvalid ||
        isWeekendBlocked ||
        isMigrationBlocked
      }
    >
      {isMigrationBlocked
        ? t(getMigrationBlockButtonKey(migrationReason))
        : isLimitPriceInvalid
          ? t('closeDialog.invalidLimitPrice')
          : t('closeDialog.placeOrder')}
    </Button>
  );

  return (
    <DialogContent
      className="gap-0 border border-border bg-background p-0 sm:max-w-[425px] sm:rounded-none"
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <FormProvider {...form}>
        <form
          ref={formRef}
          onSubmit={form.handleSubmit(onSubmit, (errors) => {
            console.error('Form validation errors:', errors);
          })}
        >
          <DialogHeader className="px-5 pt-6">
            <DialogTitle className="text-xl text-foreground">
              {t('closeDialog.title', { symbol: abbrFromSym(symbol) })}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col px-0 pb-2 pt-3">
            <Tabs
              defaultValue={OrderType.MARKET}
              className="w-full"
              value={orderType}
              onValueChange={onOrderTypeChange}
            >
              <TabsList>
                <TabsTrigger
                  value={OrderType.MARKET}
                  className="overflow-hidden border-b border-border data-[state=active]:border-primary data-[state=active]:text-primary"
                >
                  {t('closeDialog.market')}
                </TabsTrigger>
                <TabsTrigger
                  value={OrderType.LIMIT}
                  className="overflow-hidden border-b border-border data-[state=active]:border-primary data-[state=active]:text-primary"
                >
                  {t('closeDialog.limit')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex flex-col p-2.5">
              <div className="flex h-10 items-center justify-between px-3">
                <Label className="text-left text-base text-vestgrey-100">
                  {t('closeDialog.availableToClose')}
                </Label>
                <div className="font-mono text-vestgrey-50">
                  {`${formatBigInt(position.size, sizeDecimals)} ${abbrFromSym(symbol)} (${formatBigInt(
                    position.size * midPrice,
                    sizeDecimals + priceDecimals,
                    { digits: 2 },
                  )})`}
                </div>
              </div>
              <div className="flex h-10 items-center justify-between px-3">
                <p className="text-left text-base text-vestgrey-100">
                  {t('closeDialog.entryPrice')}
                </p>
                <div className="font-mono text-vestgrey-50">
                  {formatBigInt(position.cost / position.size, priceDecimals, {
                    style: 'currency',
                    digits: Number(priceDecimals),
                  })}
                </div>
              </div>
              {isMarketOrder ? (
                <div className="flex h-10 items-center justify-between px-3">
                  <Label
                    htmlFor="available-to-close"
                    className="text-left text-base text-vestgrey-100"
                  >
                    {t('closeDialog.closePrice')}
                  </Label>
                  <div className="font-mono text-vestgrey-50">
                    {estFillPrice === undefined
                      ? '—'
                      : formatBigInt(estFillPrice, priceDecimals, {
                          style: 'currency',
                          digits: Number(priceDecimals),
                        })}
                  </div>
                </div>
              ) : (
                <PriceInput
                  symbol={symbol}
                  onPriceChange={(limitPrice) =>
                    form.setValue('order.limitPrice', limitPrice)
                  }
                />
              )}
              <div className="mb-2">
                <SizeInput symbol={symbol} />
              </div>
              {isMarketOrder && (
                <div className="mb-2 flex h-10 items-center justify-between px-3">
                  <Label
                    htmlFor="available-to-close"
                    className="text-left text-base text-vestgrey-100"
                  >
                    {t('closeDialog.slippage')}
                  </Label>
                  <div className="flex items-center gap-3">
                    <div className="flex items-end gap-2">
                      <span
                        className={cn(
                          'text-font font-mono text-base leading-tight',
                          (
                            isFundedOrEval
                              ? slippageAmount !== null && slippageAmount <= 0n
                              : slippage &&
                                Number(Number(slippage.decimal).toFixed(2)) <= 0
                          )
                            ? 'text-green'
                            : null,
                        )}
                      >
                        {isFundedOrEval
                          ? slippageAmount === null
                            ? '—'
                            : formatBigInt(
                                slippageAmount,
                                priceDecimals + sizeDecimals,
                                { digits: 2, style: 'currency' },
                              )
                          : formatNumber(slippage?.decimal, {
                              digits: 4,
                              style: 'slippage',
                            })}
                      </span>
                      <span className="px-1 leading-none text-vestgrey-600">
                        /
                      </span>
                      <span className="text-sm leading-tight text-vestgrey-600">
                        {t('closeDialog.max')}
                      </span>
                    </div>
                    <div className="flex items-center bg-vestgrey-800">
                      <MaxSlippageInput
                        symbol={symbol}
                        isBuy={!position.isLong}
                        isSubmitSuccessful={isSubmitSuccessful}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isMarketOrder && slippage && slippage.bigint >= 10n ** 4n && (
                <div className="mx-3 flex gap-2 rounded-md border border-border bg-muted p-3">
                  <AlertTriangle size={14} className="shrink-0" />
                  <p className="text-sm text-foreground">
                    This trade may fill at a worse price than shown.
                  </p>
                </div>
              )}
              <div className="flex h-10 items-center justify-between px-3">
                <p className="text-left text-base text-vestgrey-100">
                  {t('closeDialog.funding')}
                </p>
                <div
                  className={cn(
                    'font-mono text-vestgrey-50',
                    funding.bigint < 0n && 'text-red',
                    funding.bigint > 0n && 'text-green',
                  )}
                >
                  {formatNumber(funding.decimal, {
                    showChange: true,
                    digits: 2,
                  })}
                </div>
              </div>
              <div className="flex h-10 items-center justify-between px-3">
                <p className="text-left text-base text-vestgrey-100">
                  {t('closeDialog.fee')}
                </p>
                <div className="font-mono text-vestgrey-50">
                  {formatNumber(fee.decimal, {
                    digits: 2,
                  })}
                </div>
              </div>
              <div className="flex h-10 items-center justify-between px-3">
                <p className="text-left text-base text-vestgrey-100">
                  {t('closeDialog.pnl')}
                </p>
                <div className="flex items-center gap-4">
                  <div className="text-font flex font-mono text-base">
                    {unrealizedPnl ? <PnL {...unrealizedPnl} /> : '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-2">
            {isWeekendBlocked ? (
              <div className="w-full">
                <TooltipWrapper
                  content={t('marketInfo.marketOpensInTooltip', {
                    reopen: reopenLabel,
                  })}
                >
                  {submitButton}
                </TooltipWrapper>
              </div>
            ) : (
              submitButton
            )}
          </DialogFooter>
          <OrderConfirm
            open={isConfirmOpen}
            onOpenChange={(open) => {
              setIsConfirmOpen(open);

              if (!open && !wasConfirmSubmitted.current) {
                restoreLimitPriceOnCancel();
                resetConfirmFlags();
              }

              wasConfirmSubmitted.current = false;
            }}
            onSubmit={() => {
              wasConfirmSubmitted.current = true;
              isConfirmSubmitted.current = true;

              formRef.current?.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true }),
              );
            }}
          />
          <LimitsBreachConfirm
            open={isLimitsBreachConfirmOpen}
            breachInfo={limitsBreachInfo}
            onOpenChange={(open) => {
              setIsLimitsBreachConfirmOpen(open);

              if (!open && !wasConfirmSubmitted.current) {
                restoreLimitPriceOnCancel();
                resetConfirmFlags();
              }

              wasConfirmSubmitted.current = false;
            }}
            onSubmit={() => {
              wasConfirmSubmitted.current = true;
              isLimitsBreachConfirmSubmitted.current = true;

              formRef.current?.dispatchEvent(
                new Event('submit', { cancelable: true, bubbles: true }),
              );
            }}
          />
        </form>
      </FormProvider>
    </DialogContent>
  );
};
